import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningIcon from '@mui/icons-material/Warning';
import { Alert, Box, Tab, Tabs, useTheme } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import {
  selectHasStagedChanges,
  selectLatestStatus,
  selectUsedInAnalyticalDatasets,
  useCohortDefinitionStore,
} from '@verantos-dev/app-state-cohort-definition';
import { setAllowMessageSubmit } from '@verantos-dev/app-state-research-assistant';
import { CohortBuilderPageTab } from '@verantos-dev/core-enums';
import { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';

import { useProjectCohortDetailPage } from './useProjectCohortDetailPage';
import { ProcessCohortDefinitionModal } from '../../components/ProcessCohortDefinitionModal/ProcessCohortDefinitionModal';
import { RevertCohortDefinitionModal } from '../../components/RevertCohortDefinitionModal/RevertCohortDefinitionModal';
import { SaveCohortDefinitionModal } from '../../components/SaveCohortDefinitionModal/SaveCohortDefinitionModal';
import { StagedChangesPill } from '../../components/StagedChangesPill/StagedChangesPill';
import CohortExitModal from '../../containers/v0/cohort-builder-page-layout/CohortBuilderExitModal/CohortExitModal';
import {
  CohortBuilderActionDialogType,
  CohortBuilderModalProvider,
  useCohortBuilderModalContext,
} from '../../contexts/CohortBuilderModalContext';
import { useCohortTranslationQuery } from '../api/useCohortTranslationQuery';
import { CohortDetailHeader } from '../CohortDetailHeader/CohortDetailHeader';
import { DefinitionTab } from '../DefinitionTab/DefinitionTab';
import { ResultsTab } from '../ResultsTab/ResultsTab';
import { SqlTab } from '../SqlTab/SqlTab';

const TAB_QUERY_PARAM = 'pageTab';

// M4 workspace design: SQL is a sibling tab of Definition and Results,
// gated by the sql_tab_in_cb flag (flag off keeps SQL inside Definition).
// TODO: cleanup with sql_tab_in_cb ff
type DetailTab =
  | CohortBuilderPageTab.definition
  | CohortBuilderPageTab.sql
  | CohortBuilderPageTab.results;

const VALID_TABS: ReadonlyArray<DetailTab> = [
  CohortBuilderPageTab.definition,
  CohortBuilderPageTab.sql,
  CohortBuilderPageTab.results,
];

// Tab labels match the AD header tabs (and the Figma <Tab> spec): body2
// 14px medium — the theme-derived button variant renders 16px otherwise.
const TAB_SX = {
  typography: 'body2',
  fontWeight: 500,
  letterSpacing: '0.4px',
  color: 'text.secondary',
  '&.Mui-selected': { color: 'primary.main' },
  '&:hover': { color: 'text.primary' },
} as const;

const parseTab = (raw: string | null, isSqlTabEnabled: boolean): DetailTab => {
  if (raw && (VALID_TABS as ReadonlyArray<string>).includes(raw)) {
    // Deep links to ?pageTab=sql fall back to Definition while the flag is off.
    if (raw === CohortBuilderPageTab.sql && !isSqlTabEnabled) {
      return CohortBuilderPageTab.definition;
    }
    return raw as DetailTab;
  }
  return CohortBuilderPageTab.definition;
};

/**
 * Gen-3 cohort detail page. Mounted at the agent-first cohort route under
 * the `research_assistant_gen_3` flag. Replaces the legacy
 * `CohortBuilderPage` for that route only — legacy `cohorts/...` tree
 * keeps `CohortBuilderPage` unchanged.
 *
 * This file is the entry point for the entire `gen-3/` bucket. Subsequent
 * commits land the tab switcher (Definition | Results), AV-only
 * Definition sections, and responsive Results cards in their own sibling
 * folders next to this one.
 *
 * TODO: cleanup with research_assistant_gen_3 ff (collapse the route gate
 * once the flag is fully rolled out and delete the legacy v0/ tree).
 */
export const ProjectCohortDetailPage = () => {
  return (
    <CohortBuilderModalProvider>
      <ProjectCohortDetailPageBody />
    </CohortBuilderModalProvider>
  );
};

const ProjectCohortDetailPageBody = () => {
  const theme = useTheme();
  const intl = useIntl();
  const { activeDialog } = useCohortBuilderModalContext();
  const [searchParams, setSearchParams] = useSearchParams();
  // TODO: cleanup with sql_tab_in_cb ff
  const isSqlTabEnabled = Boolean(useFeatureFlagSplit('sql_tab_in_cb'));
  // TODO: cleanup with asset_edit_while_in_use ff — checked once here (the
  // top-level page component) and threaded down as a plain boolean so the
  // orchestration hook doesn't need its own flag check.
  const isAssetEditWhileInUseEnabled = Boolean(useFeatureFlagSplit('asset_edit_while_in_use'));
  // TODO: cleanup with cohort_ui_optimised ff — off: every section (header,
  // Study period, Criteria) waits on the OR of all three loading flags
  // (byte-identical to the pre-flag behavior). On: each section gates only
  // on the query it actually reads, so it isn't held back by an unrelated
  // slower call.
  const isCohortUiOptimised = Boolean(useFeatureFlagSplit('cohort_ui_optimised'));
  const {
    cohortProfile,
    isProfileLoading,
    isDefinitionLoading,
    isHydrationLoading,
    showStaleAnalyticalVariablesWarning,
    projectId,
    cohortId,
    cohortVersionId,
  } = useProjectCohortDetailPage({ isAssetEditWhileInUseEnabled });
  // TODO: cleanup with cohort_ui_optimised ff
  const legacyIsLoading = isProfileLoading || isHydrationLoading;
  const headerIsLoading = isCohortUiOptimised ? isProfileLoading : legacyIsLoading;
  const studyPeriodIsLoading = isCohortUiOptimised ? isDefinitionLoading : legacyIsLoading;
  const criteriaIsLoading = isCohortUiOptimised ? isHydrationLoading : legacyIsLoading;
  const activeTab = parseTab(searchParams.get(TAB_QUERY_PARAM), isSqlTabEnabled);
  // Warm the SQL translation cache on cohort entry. The SQL tab's (and the
  // flag-off Definition SQL section's) useSqlSection shares this query key,
  // so opening the tab reads the cache instead of firing the first
  // /translate then. The query stays disabled until the store hydrates
  // criteria, so this never races hydration; result intentionally unused.
  useCohortTranslationQuery({ projectId, cohortId, cohortVersionId });
  const hasStagedChanges = useCohortDefinitionStore(selectHasStagedChanges);
  const latestStatus = useCohortDefinitionStore(selectLatestStatus);
  // TODO: cleanup with asset_edit_while_in_use ff. Only locks read-only while
  // the flag is off; with the flag on the cohort stays editable and no banner
  // is shown.
  const usedInAnalyticalDatasets = useCohortDefinitionStore(selectUsedInAnalyticalDatasets);
  const isAdLocked = !isAssetEditWhileInUseEnabled && usedInAnalyticalDatasets.length > 0;

  // Warn on tab close / hard refresh when staged changes exist. In-app
  // navigation interception still flows through breadcrumb-owned guards
  // (the parent ProjectPage owns the breadcrumb); the modal is mounted
  // below so any future caller can `openDialog(EXIT_COHORT_DEFINITION,
  // { exitAction })`.
  useEffect(() => {
    if (!hasStagedChanges) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasStagedChanges]);

  // Disable RA chat submission while the cohort is in a terminal state
  // (FINAL), actively processing, or locked by analytical-dataset usage
  // (OR-6192, only while `asset_edit_while_in_use` is off) — matches the
  // legacy CohortBuilderPage gate. Always re-enable on unmount so navigating
  // away from a cohort restores normal chat behavior elsewhere.
  useEffect(() => {
    const allow =
      !isAdLocked &&
      (!latestStatus ||
        ![CohortDefinitionStatus.FINAL, CohortDefinitionStatus.PROCESSING].includes(latestStatus));
    setAllowMessageSubmit(allow);
    return () => setAllowMessageSubmit(true);
  }, [latestStatus, isAdLocked]);

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, next: DetailTab) => {
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          if (next === CohortBuilderPageTab.definition) {
            updated.delete(TAB_QUERY_PARAM);
          } else {
            updated.set(TAB_QUERY_PARAM, next);
          }
          return updated;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return (
    <Box
      data-testid="project-cohort-detail-page"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        minHeight: 0,
        // Positioning anchor for the absolutely-positioned StagedChangesPill so
        // it centers within the cohort area, not the whole page (chat included).
        position: 'relative',
      }}
    >
      <CohortDetailHeader cohortProfile={cohortProfile} isLoading={headerIsLoading} />
      {/* TODO: cleanup with asset_edit_while_in_use ff. Info banner — explains
          (not warns) why the cohort is read-only while used by a dataset.
          Only rendered while the flag is off. */}
      {isAdLocked && (
        <Alert
          severity="info"
          icon={<InfoOutlinedIcon fontSize="inherit" />}
          sx={{ py: 0.5, borderRadius: 0 }}
          data-testid="cohort-ad-lock-banner"
        >
          {intl.formatMessage(
            { id: 'projects.cohortBuilderPage.analyticalDatasetLock.banner' },
            {
              count: usedInAnalyticalDatasets.length,
              datasets: usedInAnalyticalDatasets.map((ad) => ad.name).join(', '),
            }
          )}
        </Alert>
      )}
      <Box sx={{ paddingX: 6 }}>
        {/* Divider sits on the Tabs so it starts at the content gutter
            (Figma / AD header parity), not at the panel edge. */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="Cohort detail tabs"
          data-testid="cohort-detail-tabs"
          sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
        >
          {/* First tab flush-left so the label + indicator align with the
              content gutter (same treatment as the AD header tabs). */}
          <Tab
            value={CohortBuilderPageTab.definition}
            label="Definition"
            data-testid="cohort-detail-tab-definition"
            sx={{ ...TAB_SX, pl: 0, minWidth: 0 }}
          />
          {isSqlTabEnabled && (
            <Tab
              value={CohortBuilderPageTab.sql}
              label="SQL"
              data-testid="cohort-detail-tab-sql"
              sx={TAB_SX}
            />
          )}
          <Tab
            value={CohortBuilderPageTab.results}
            label="Results"
            data-testid="cohort-detail-tab-results"
            sx={TAB_SX}
          />
        </Tabs>
      </Box>
      {/* An analytical variable this cohort depends on has changed
          since this cohort was last saved/processed. Status stays PROCESSED.
          TODO: cleanup with av_edit_while_in_use ff */}
      {showStaleAnalyticalVariablesWarning && (
        <Alert
          severity="warning"
          icon={<WarningIcon fontSize="inherit" />}
          sx={{ py: 0.5, mx: 6, mt: 3, borderRadius: 0 }}
          data-testid="cohort-stale-av-warning"
        >
          {intl.formatMessage({ id: 'projects.cohortBuilderPage.staleAnalyticalVariablesWarning' })}
        </Alert>
      )}
      {/* No page-level hard-fail overlay: each section degrades on its
          own (header shows '—', SQL shows the empty placeholder, etc).
          Blanket-erroring the page hid working content (Study period,
          Criteria) while a single hook had a soft failure. */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {activeTab === CohortBuilderPageTab.definition && (
          <DefinitionTab
            isStudyPeriodLoading={studyPeriodIsLoading}
            isCriteriaLoading={criteriaIsLoading}
          />
        )}
        {isSqlTabEnabled && activeTab === CohortBuilderPageTab.sql && <SqlTab />}
        {activeTab === CohortBuilderPageTab.results && <ResultsTab />}
      </Box>
      {activeDialog === CohortBuilderActionDialogType.PROCESS_COHORT_DEFINITION && (
        <ProcessCohortDefinitionModal />
      )}
      {activeDialog === CohortBuilderActionDialogType.SAVE_COHORT_DEFINITION && (
        <SaveCohortDefinitionModal />
      )}
      {activeDialog === CohortBuilderActionDialogType.REVERT_COHORT_DEFINITION && (
        <RevertCohortDefinitionModal />
      )}
      {/* CohortExitModal is mounted but currently dormant: with autosave
          (explicit_save) on, navigation-time exit confirmation is
          obsolete — the StagedChangesPill is the canonical affordance
          for unsaved / failed-save state and resolves both inline.
          Nothing in gen-3 calls openDialog(EXIT_COHORT_DEFINITION); the
          mount is kept so the legacy dialog wiring can be reached if a
          future surface needs it. See StagedChangesPill JSDoc. */}
      {activeDialog === CohortBuilderActionDialogType.EXIT_COHORT_DEFINITION && <CohortExitModal />}
      <StagedChangesPill />
    </Box>
  );
};

export default ProjectCohortDetailPage;
